import React, { useState } from "react";
import { Badge } from "../ui/badge";
import Image from "../Tags/Image";
import { MoreVertical } from "lucide-react";
import useModal from "../GenericModal/useModal";
import GenericModal from "../GenericModal";
import { deleteCoachAIBot } from "../../apis/callLogs";
import { useMutation, useQueryClient } from "react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { Dialog } from "@headlessui/react";
import toast from "react-hot-toast";
import { useCaptureEvent } from "../../hooks/useCaptureEvent";

export default function BotCard({
  data,
  isActive = false,
  onSelect,
  disabled = false,
  activeWorkspace = {},
  userId = "",
}) {
  const queryClient = useQueryClient();
  const captureEvent = useCaptureEvent();
  const returnCallType = (callType) => {
    if (callType === "cold") {
      return (
        <Badge radius="sm" variant="gray">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-1"
          >
            <g clip-path="url(#clip0_13070_33319)">
              <path
                d="M9.02918 4.25L2.96701 7.75M9.02918 4.25L9.57822 2.20096M9.02918 4.25L11.0782 4.79904M2.96701 7.75L0.917969 7.20096M2.96701 7.75L2.41797 9.79904M9.02914 7.74993L2.96696 4.24993M9.02914 7.74993L11.0782 7.2009M9.02914 7.74993L9.57823 9.79898M2.96696 4.24993L2.41804 2.20102M2.96696 4.24993L0.91804 4.79909M5.9981 2.5L5.9981 9.5M5.9981 2.5L4.4981 1M5.9981 2.5L7.4981 1M5.9981 9.5L4.4981 11M5.9981 9.5L7.4981 11"
                stroke="#535862"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_13070_33319">
                <rect width="12" height="12" fill="white" />
              </clipPath>
            </defs>
          </svg>
          Cold call
        </Badge>
      );
    } else if (callType === "discovery") {
      return (
        <Badge radius="sm" variant="gray">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-1"
          >
            <g clip-path="url(#clip0_13070_32519)">
              <path
                d="M10.5 10.5L8.325 8.325M9.5 5.5C9.5 7.70914 7.70914 9.5 5.5 9.5C3.29086 9.5 1.5 7.70914 1.5 5.5C1.5 3.29086 3.29086 1.5 5.5 1.5C7.70914 1.5 9.5 3.29086 9.5 5.5Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_13070_32519">
                <rect width="12" height="12" fill="white" />
              </clipPath>
            </defs>
          </svg>
          Discovery call
        </Badge>
      );
    }
  };

  const returnTone = (tone) => {
    if (tone === "rude") {
      return (
        <Badge variant="error-outline">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-1"
          >
            <path
              d="M7.5 4.5H7.505M4.5 4.5H4.505M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6ZM7.75 4.5C7.75 4.63807 7.63807 4.75 7.5 4.75C7.36193 4.75 7.25 4.63807 7.25 4.5C7.25 4.36193 7.36193 4.25 7.5 4.25C7.63807 4.25 7.75 4.36193 7.75 4.5ZM4.75 4.5C4.75 4.63807 4.63807 4.75 4.5 4.75C4.36193 4.75 4.25 4.63807 4.25 4.5C4.25 4.36193 4.36193 4.25 4.5 4.25C4.63807 4.25 4.75 4.36193 4.75 4.5ZM6 8.75C7.25025 8.75 8.25 7.8335 8.25 7H3.75C3.75 7.8335 4.74975 8.75 6 8.75Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Rude
        </Badge>
      );
    } else if (tone === "cheerful") {
      return (
        <Badge variant="blue-outline">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-1"
          >
            <path
              d="M7.5 4.5H7.505M4.5 4.5H4.505M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6ZM7.75 4.5C7.75 4.63807 7.63807 4.75 7.5 4.75C7.36193 4.75 7.25 4.63807 7.25 4.5C7.25 4.36193 7.36193 4.25 7.5 4.25C7.63807 4.25 7.75 4.36193 7.75 4.5ZM4.75 4.5C4.75 4.63807 4.63807 4.75 4.5 4.75C4.36193 4.75 4.25 4.63807 4.25 4.5C4.25 4.36193 4.36193 4.25 4.5 4.25C4.63807 4.25 4.75 4.36193 4.75 4.5ZM6 8.75C7.25025 8.75 8.25 7.8335 8.25 7H3.75C3.75 7.8335 4.74975 8.75 6 8.75Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Cheerful
        </Badge>
      );
    } else if (tone === "neutral") {
      return (
        <Badge variant="gray-outline">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-1"
          >
            <path
              d="M7.5 4.5H7.505M4.5 4.5H4.505M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6ZM7.75 4.5C7.75 4.63807 7.63807 4.75 7.5 4.75C7.36193 4.75 7.25 4.63807 7.25 4.5C7.25 4.36193 7.36193 4.25 7.5 4.25C7.63807 4.25 7.75 4.36193 7.75 4.5ZM4.75 4.5C4.75 4.63807 4.63807 4.75 4.5 4.75C4.36193 4.75 4.25 4.63807 4.25 4.5C4.25 4.36193 4.36193 4.25 4.5 4.25C4.63807 4.25 4.75 4.36193 4.75 4.5ZM6 8.75C7.25025 8.75 8.25 7.8335 8.25 7H3.75C3.75 7.8335 4.74975 8.75 6 8.75Z"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Neutral
        </Badge>
      );
    }
  };

  const getInitials = (name = "") => {
    if (!name) return "";

    const words = name.trim().split(/\s+/); // split by spaces
    if (words.length === 1) {
      return words[0][0].toUpperCase();
    }

    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const [selectedBotToDelete, setSelectedBotToDelete] = useState({});
  const { isOpen: isOpenDelete, openModal: openModalDelete, closeModal: closeModalDelete } = useModal(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const deleteCoachAIBotFn = useMutation("delete_bot", deleteCoachAIBot, {
    onSuccess(data, variables, context) {
      toast.success(data?.message);
      captureEvent("CAI_DeleteBot_Success", { bot_id: variables?.botId });
      closeModalDelete();
      queryClient.invalidateQueries("get_coachbots");
    },
    onError(error, variables) {
      toast.error(error?.message || "Failed to delete");
      captureEvent("CAI_DeleteBot_Failure", {
        bot_id: variables?.botId,
        error: error?.message || "unknown_error",
      });
    },
  });
  const handleDeleteBot = () => {
    captureEvent("CAI_DeleteBot_SubmitClick", { bot_id: selectedBotToDelete?.id });
    deleteCoachAIBotFn.mutate({
      botId: selectedBotToDelete?.id,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  return (
    <>
      {data && data.id && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => {
            if (disabled) return;
            onSelect?.(data);
          }}
          className={`border p-3 rounded-xl flex items-start justify-between gap-3 min-h-[88px] relative w-full text-left transition-all ${
            isActive
              ? "bg-brand-50 border-brand-300 shadow-sm"
              : "bg-background border-border hover:border-brand-200"
          } ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
        >
          <div className="inline-flex items-start space-x-3">
            {/* <div className="h-9 w-9 flex-shrink-0 items-center bg-gray-100 border border-border justify-center inline-flex rounded-full text-xs">
              {data?.first_name && getInitials(`${data?.first_name} ${data?.last_name}`)}
            </div> */}
            {data?.avatar && !data?.avatar.includes("https://") && !data?.avatar.includes(".jpg") ? (
              <Image
                src={`/images/bots/${data?.avatar && data?.avatar.includes(".svg") ? data?.avatar : `${data?.avatar}.svg`}`}
                width={40}
                height={40}
                className="rounded w-10 h-10 no-invert"
              />
            ) : (
              <Image src={`/images/bots/avatar_1.svg`} width={40} height={40} className="rounded w-10 h-10" />
            )}
            <div>
              <span className="text-[14px] text-gray-950 block font-bold">
                {data?.first_name} {data?.last_name}
              </span>
              <span className="text-[12px] text-gray-900 block mt-0.5">{data?.role}</span>
              <div className="mt-2 flex space-x-2">
                {returnCallType(data?.call_type)}
                {returnTone(data?.tone)}
              </div>
            </div>
          </div>
          {!data?.is_default &&
            (userId === data?.created_by ||
              activeWorkspace.role === "Admin" ||
              activeWorkspace.role === "Owner") && (
              <div className="">
                {disabled ? (
                  <div className="h-8 w-8 inline-flex items-center justify-center text-border">
                    <MoreVertical className="h-5 w-5" />
                  </div>
                ) : (
                  <DropdownMenu open={isMenuOpen} onOpenChange={(open) => setIsMenuOpen(open)}>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[#535862] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-200"
                        onClick={() => {
                          setSelectedBotToDelete(data);
                          captureEvent("CAI_DeleteBot_Open", { bot_id: data?.id });
                          openModalDelete();
                        }}
                      >
                        <MoreVertical className="h-5 w-5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      {/* <DropdownMenuItem>Edit</DropdownMenuItem> */}
                      <DropdownMenuItem
                        onClick={() => {
                          console.log("data", data);
                          setSelectedBotToDelete(data);
                          openModalDelete();
                        }}
                        className="text-error-600 focus:text-error-600"
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            )}
        </button>
      )}

      <GenericModal isOpen={isOpenDelete} closeModal={closeModalDelete}>
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
              {selectedBotToDelete.first_name} {selectedBotToDelete.last_name} will be Deleted! <br /> Are you
              Sure?
            </Dialog.Title>
            <div className="mt-2">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete your{" "}
                <b>
                  {selectedBotToDelete.first_name} {selectedBotToDelete.last_name}
                </b>
                ? All of your data will be permanently removed from our servers forever. This action cannot be
                undone.
              </p>
            </div>
          </div>
        </div>
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            disabled={deleteCoachAIBotFn.isLoading}
            className="inline-flex items-center w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
            onClick={handleDeleteBot}
          >
            Delete {deleteCoachAIBotFn.isLoading && <div className="loader_circle ml-2"></div>}
          </button>
          <button
            type="button"
            className="mt-3 inline-flex w-full justify-center rounded-md bg-background px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
            onClick={() => closeModalDelete()}
          >
            Cancel
          </button>
        </div>
      </GenericModal>
    </>
  );
}
